import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import { cn } from "@/lib/utils";

interface AvatarDetailsButtonProps {
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  icon?: React.ReactNode;
  /** Fill of the icon circle as a colour value; for user-picked colours (group, label). */
  iconBgColor?: string;
  /** Fill and icon colour as token classes; wins over `iconBgColor`. */
  iconClassName?: string;
  onClick?: () => void;
  className?: string;
  title?: string;
  hideDetailsLabel?: boolean;
  nonClickable?: boolean;
  subtitle?: string;
}

/**
 * AvatarDetailsButton - Reusable component for displaying avatars with optional navigation
 *
 * Features:
 * - Rounded frame matching table border color
 * - Avatar with initials fallback
 * - Optional "Details" text for intuitive navigation
 * - Hover effects for better UX (when clickable)
 * - Display-only mode for non-interactive contexts
 *
 * Usage across object types:
 * - Tables: <AvatarDetailsButton firstName={emp.firstname} onClick={navigate} />
 * - Selectors: <AvatarDetailsButton firstName={contact.firstname} nonClickable hideDetailsLabel />
 * - Forms: <AvatarDetailsButton firstName={parent.firstname} subtitle={email} nonClickable />
 */
export function AvatarDetailsButton({
  firstName,
  lastName,
  avatarUrl,
  icon,
  iconBgColor,
  iconClassName,
  onClick,
  className = "",
  title,
  hideDetailsLabel = false,
  nonClickable = false,
  subtitle,
}: AvatarDetailsButtonProps) {
  const { i18n } = useLingui();
  const initials = `${firstName?.charAt(0) || ""}${
    lastName?.charAt(0) || ""
  }`.toUpperCase();
  // Whatever name parts exist, joined without a stray space: "Anna Muster",
  // "Anna" or "Muster". Empty when the entity carries no name at all.
  const fullName = [firstName, lastName]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0)
    .join(" ");
  // The "Details" word is a navigation hint for the clickable table cell. A
  // display-only cell (selectors, pickers) has nothing to navigate to, so it
  // shows the name — a contact without an email used to render as "Details"
  // in the add-relationship picker, hiding who the row was.
  const showName = Boolean(subtitle) || nonClickable;
  const labelText = showName && fullName ? fullName : i18n._(t`Details`);

  const defaultTitle = title || i18n._(t`Edit {firstName} {lastName}`, { firstName, lastName });
  
  // Content that's shared between button and div
  const content = (
    <>
      {icon ? (
        <div
          className={cn(
            "h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center",
            iconClassName ?? (iconBgColor ? undefined : "bg-primary text-primary-foreground")
          )}
          style={!iconClassName && iconBgColor ? { backgroundColor: iconBgColor } : undefined}
        >
          {icon}
        </div>
      ) : (
        <Avatar className="h-7 w-7 flex-shrink-0">
          <AvatarImage
            src={avatarUrl || undefined}
            alt={`${firstName} ${lastName}`}
          />
          <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>
      )}
      {!hideDetailsLabel && (
        <div className="flex flex-col">
          <span className={`text-xs font-medium ${nonClickable ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'} transition-colors`}>
            {labelText}
          </span>
          {subtitle && (
            <span className="text-xs text-muted-foreground">
              {subtitle}
            </span>
          )}
        </div>
      )}
    </>
  );

  // Render as non-clickable div if nonClickable is true
  if (nonClickable) {
    return (
      <div
        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border ${className}`}
      >
        {content}
      </div>
    );
  }

  // Render as button for clickable version
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors group ${className}`}
      title={defaultTitle}
    >
      {content}
    </button>
  );
}
